import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/tailwind.util';
import { type BottomLabelProps, TextLength, type TopLabelProps } from '../label';
import { WithLabel } from '../label/WithLabel';
type TextFieldVariants = VariantProps<typeof textFieldVariants>;
const textFieldVariants = cva(cn('group bg-white relative flex w-full px-sm rounded-sm border border-alternative', 'group-[:has(:invalid)]:invalid:border-transparent group-[:has(:invalid)]:invalid:outline group-[:has(:invalid)]:invalid:outline-destructive-default'), {
  variants: {
    intent: {
      neutral: 'h-[48px] py-sm text-body-lg-medium',
      strong: 'h-[58px] py-md text-heading-sm-bold'
    },
    // ZUS 의 TextField size 축. intent(강조) 와 축이 달라 따로 둔다.
    // sm 은 목록 안의 검색 필드처럼 조밀한 자리에 쓰는 크기다.
    // (py-xs 8 + body-lg-medium 22.4 + border 2 = 40.4 → h-[40px])
    // intent 뒤에 선언해야 cn(tailwind-merge) 에서 높이/패딩이 이 값으로 덮인다.
    size: {
      md: '',
      sm: 'h-[40px] py-xs'
    },
    error: {
      true: 'border-transparent ring-1 ring-inset ring-destructive-default',
      false: 'focus-within:border-transparent focus-within:ring-2 focus-within:ring-inset focus-within:ring-strong-default'
    },
    disabled: {
      true: 'bg-alternative',
      false: ''
    }
  },
  defaultVariants: {
    intent: 'neutral',
    size: 'md',
    error: false,
    disabled: false
  }
});

// 'size' 도 함께 뺀다. input 의 기본 size 는 number(문자 폭)라, 아래에서 선언하는
// 디자인 시스템의 size('md' | 'sm') 와 교차하면 never 가 된다.
type TextFieldProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'size'> & TextFieldVariants & Omit<TopLabelProps, 'optional'> & Partial<Omit<BottomLabelProps, 'error'>> & {
  leftSlot?: React.JSX.Element | string;
  rightSlot?: React.JSX.Element | string;
  value?: string;
  intent?: 'neutral' | 'strong';
  size?: 'md' | 'sm';
  error?: boolean;
  showTextLength?: boolean;
};

/**
 * ZEP UI V3 텍스트필드 컴포넌트
 * @param intent 텍스트필드 강조 정도
 * @param title 텍스트필드 상단 제목
 * @param titleLeftSlot 텍스트필드 상단 제목 왼쪽 슬롯
 * @param titleRightIcon 텍스트필드 상단 제목 오른쪽 슬롯
 * @description title slot의 경우 컴포넌트의 제한은 없으나, 디자인 정합성을 위해 TextFieldSlotIcon 컴포넌트를 사용할 것을 권장합니다.
 * @param description 텍스트필드 상단 부가 설명
 * @param leftSlot 텍스트필드 인풋 왼쪽 슬롯
 * @param rightSlot 텍스트필드 인풋 오른쪽 슬롯
 * @description input slot의 경우 컴포넌트의 제한은 없으나, 디자인 정합성을 위해 TextFieldSlotIcon, TextFieldSlotText, TextFieldSlotButton 컴포넌트를 사용할 것을 권장합니다.
 * @param helperText 텍스트필드 하단 도움말 텍스트
 * @param error 텍스트필드 에러 상태
 * @param showTextLength 텍스트필드 텍스트 길이 표시 여부
 * @example
 * <TextField
 *  intent="neutral"
 *  title="제목"
 *  titleLeftIcon={<TextFieldTitleSlotIcon icon={BookFillIcon} />}
 *  titleRightIcon={<TextFieldTitleSlotIcon icon={ChevronRightIcon} />}
 *  description="부가 설명"
 *  leftSlot={<TextFieldSlotIcon icon={BookFillIcon} />}
 *  rightSlot={<TextFieldSlotText text={'%'} />}
 *  helperText="도움말 텍스트"
 *  error={false}
 *  {...props}
 */
const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(({
  className,
  intent = 'neutral',
  size = 'md',
  title,
  titleLeftSlot,
  titleRightSlot,
  description,
  leftSlot,
  rightSlot,
  helperText,
  error = false,
  showTextLength = false,
  required = false,
  ...props
}, ref) => {
  //disabled와 같은 textfield로 전달되는 props를 slot에도 연동시키기 위해,
  //textfield 내부에서 slot을 clone하여 props를 주입합니다.
  const cloneWithProps = (element: React.ReactNode, additionalProps: {
    disabled?: boolean;
  }) => {
    if (React.isValidElement(element)) {
      return React.cloneElement(element, additionalProps);
    }
    return element;
  };
  const disabledLeftSlot = cloneWithProps(leftSlot, {
    disabled: true
  });
  const disabledRightSlot = cloneWithProps(rightSlot, {
    disabled: true
  });
  return <WithLabel title={title} titleLeftSlot={titleLeftSlot} titleRightSlot={titleRightSlot} optional={!required} description={description} rightSlot={showTextLength && Boolean(props.maxLength) && <div className="flex self-end pb-4xs">
              <TextLength textLength={props.value?.length ?? 0} maxTextLength={props.maxLength} />
            </div>} helperText={helperText} error={error}>
        <div className={cn(textFieldVariants({
      intent,
      size,
      className,
      error,
      disabled: props.disabled
    }), className)}>
          <div className="relative flex w-full items-center gap-xs">
            {props.disabled ? disabledLeftSlot : leftSlot}
            <input type="text" ref={ref} className={cn('block grow w-full h-full bg-transparent', '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none')} {...props} />
            {props.disabled ? disabledRightSlot : rightSlot}
          </div>
        </div>
      </WithLabel>;
});
TextField.displayName = 'TextField';
export { TextField, textFieldVariants };